import { useState, useRef, useEffect } from "react";
import type { NavbarProps } from "@/types/layout";
import { Input } from "@/components/ui/input";
import HamburgerIcon from "./icons/HamburgerIcon";
import SearchIconMobile from "./icons/SearchIconMobile";
import PhoneIcon from "./icons/PhoneIcon";
import CartIconMobile from "./icons/CartIconMobile";
import SearchIcon from "./SearchIcon";
import { UserProfileDropdown } from "./UserProfileDropdown";

export function MobileNavbar({
  cartItemCount,
  onToggleSidebar,
  onSearchChange,
  onDownloadAppClick,
  onCartClick,
  onLogoClick,
  searchQuery,
  setSearchQuery,
  handleKeyDown,
  handleFocus,
}: NavbarProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const handleSearchToggle = () => {
    setIsSearchOpen(!isSearchOpen);
    if (isSearchOpen) {
      setSearchQuery("");

    }
  };

 

  const handleSearchBlur = () => {
    // Delay closing to allow clicking on search results if any
    setTimeout(() => {
      if (!searchQuery) {
        setIsSearchOpen(false);
      }
    }, 200);
  };

  return (
    <nav className="w-full bg-navbar-bg border-b border-navbar-border shrink-0 md:hidden">
      {/* Main navbar row */}
      <div className="flex items-center justify-between px-3 py-3">
        {/* Left section: Hamburger + Logo */}
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleSidebar}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Toggle sidebar"
          >
            <HamburgerIcon width={17} height={13} color="rgba(0, 0, 0, 0.5)" />
          </button>

          <div
            onClick={onLogoClick}
            className="cursor-pointer active:opacity-70 transition-opacity"
            role="button"
            aria-label="Go to home"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onLogoClick?.();
              }
            }}
          >
            <img
              src="/images/navbar/auqli-logo.svg"
              alt="Auqli Logo"
              className="h-[19px] w-auto"
            />
          </div>
        </div>

        {/* Right section: Search, Phone, Cart, Avatar */}
        <div className="flex items-center gap-px">
          <button
            onClick={handleSearchToggle}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            aria-label="Search"
          >
            <SearchIconMobile
              width={20}
              height={20}
              color="rgba(0, 0, 0, 0.5)"
            />
          </button>

          <>
            <button
              onClick={onDownloadAppClick}
              className="p-2 hover:bg-gray-100 rounded transition-colors"
              aria-label="Download app"
            >
              <PhoneIcon width={14} height={20} color="rgba(0, 0, 0, 0.5)" />
            </button>

            <button
              onClick={onCartClick}
              className="relative p-2 hover:bg-gray-100 rounded transition-colors"
              aria-label="Cart"
            >
              <CartIconMobile
                width={18}
                height={20}
                color="rgba(0, 0, 0, 0.5)"
              />
              {cartItemCount > 0 && (
                <span className="absolute top-0 right-0 flex items-center justify-center w-4 h-4 text-[8px] font-lexend font-normal tracking-[-0.13px] capitalize text-white bg-red-600 rounded-full">
                  {cartItemCount}
                </span>
              )}
            </button>

            <UserProfileDropdown />
          </>
        </div>
      </div>

      {/* Expandable search bar */}
      {isSearchOpen && (
        <div className="px-3 pb-3 animate-in slide-in-from-top-2 duration-200">
          <div className="relative flex items-center w-full">
            <div className="absolute left-[11px] pointer-events-none">
              <SearchIcon width={17} height={17} color="rgba(0, 0, 0, 0.50)" />
            </div>
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="search"
              value={searchQuery}
              onChange={ onSearchChange}
              onBlur={handleSearchBlur}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              className="w-full pl-[2rem] bg-search-bg border-search-border rounded-[5px] text-[15px] font-lexend font-light tracking-[-0.22px] capitalize placeholder:text-search-text placeholder:capitalize"
            />
          </div>
        </div>
      )}
    </nav>
  );
}
